import React, { useEffect, useState } from "react";
import Breadcrums from "../components/breadcrums";
import { timesheetDetails } from "../api/timesheet-api";
import { listOfEmployees } from "../api/consultants-api";
import CommonFilters from "../blocks/commonFilters";
import classNames from "classnames";

import PageTitle from "../components/pageTitle";
import { timesheetBreadcrums } from "../constants/breadcrums";
import { sectionClass, timesheetCommands } from "../constants/common";
import TanstackTable from "../components/TanstackTable";
import { ColumnDef } from "@tanstack/react-table";
import { TimesheetType } from "../model";
import { Link } from "react-router-dom";

export default function Timesheet() {
    const [timesheetdata, setTimesheetData] = useState<TimesheetType[]>([]);

    const [isLoading, setLoading] = React.useState(false);
    const [people, setPeople] = useState<{ entity: string; active?: number }[]>([]);
    const [activeStatus, setActiveStatus] = useState<"all" | "active" | "inactive">("active");

    useEffect(() => {
        console.log("Timesheet component mounted");

        // Load list of employees
        listOfEmployees()
            .then((data) => setPeople(data))
            .catch((error) => console.error("Error loading employees:", error));

        // Load initial timesheet data
        setLoading(true);
        timesheetDetails({
            checkDate: new Date(),
            activeStatus: activeStatus,
        })
            .then((data) => {
                console.log("Initial timesheet data:", data);
                setTimesheetData(data);
            })
            .catch((error) => {
                console.error("Error loading timesheet data:", error);
                setTimesheetData([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [activeStatus]);

    function applyFilters(filters): any {
        setLoading(true);
        setTimesheetData([]);

        // Add activeStatus to the filters
        const filtersWithActiveStatus = {
            ...filters,
            activeStatus: activeStatus
        };

        console.log("Applying filters:", filtersWithActiveStatus);
        timesheetDetails(filtersWithActiveStatus)
            .then((data) => {
                console.log("Filtered timesheet data:", data);
                setTimesheetData(data);
            })
            .catch((error) => {
                console.error("Error applying filters:", error);
                setTimesheetData([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const columns = React.useMemo<ColumnDef<TimesheetType, any>[]>(
        () => [
            {
                accessorKey: "name",
                header: "Name",
                cell: (info) => info.getValue(),
                enableColumnFilter: false,
                enableGrouping: false,
                footer: ({ table }) => (
                    <>
                        <span className="font-semibold text-md">Total</span>
                    </>
                ),
            },

            {
                accessorKey: "month",
                header: "Month",
                cell: (info) => info.getValue(),
                enableColumnFilter: false,
                enableGrouping: false,
                meta: {},
            },

            {
                accessorKey: "period_start",
                header: "Period Start",
                cell: (info) => info.getValue(),
                enableColumnFilter: false,
                enableGrouping: false,
                meta: {},
            },
            {
                accessorKey: "period_end",
                header: "Period End",
                cell: (info) => info.getValue(),
                enableColumnFilter: false,
                enableGrouping: false,
                meta: {},
            },

            {
                accessorKey: "totalHrs",
                header: "Efforts (Hrs)",
                cell: (info) => Number(info.getValue()) | 0,
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <>
                        <span className="text-md">
                            {table
                                .getFilteredRowModel()
                                .rows.reduce((total, row) => total + row.original.totalHrs, 0)
                                .toLocaleString()}
                        </span>
                    </>
                ),
            },
        ],
        []
    );

    function addTimesheet(): any {}
    return (
        <>
            <main>
                <Breadcrums breadcrumLinks={timesheetBreadcrums} commands={timesheetCommands} module={"Timesheet"} groups={["Actions", "Statement"]} />
                <PageTitle title={"Timesheet"} preSummary={"Organizational Overview"} summary={"Overview of monthly total of individual employee."}></PageTitle>

                <div className={classNames(sectionClass)}>
                    {/* Dropdown to filter employees by status */}
                    <div className="mb-4">
                        <label>Filter Employees(All/Active/inactive):</label>
                        <select onChange={(e) => setActiveStatus(e.target.value as "all" | "active" | "inactive")} value={activeStatus} className="block px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm w-96 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                            <option value="all">All Employees</option>
                            <option value="active">Active Employees</option>
                            <option value="inactive">Inactive Employees</option>
                        </select>
                    </div>

                    <CommonFilters 
                        key={`${activeStatus}`} // Force remount when filter changes
                        people={people.filter((employee) => {
                            // Filter by active status
                            const statusMatch = activeStatus === "active" ? employee?.active === 1 && employee.entity === "erp360" : activeStatus === "inactive" ? employee?.active === 0 && employee.entity === "erp360" : employee?.entity === "erp360";
                            return statusMatch;
                        })} 
                        applyFilters={applyFilters} 
                        clientId={null} 
                    />
                    <div className="pt-6">
                        <Link to="/timesheet/bulkInsert">
                            <button type="button" onClick={(e) => addTimesheet()} className="px-4 py-2 text-sm font-medium text-white bg-blue-700 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-0 focus:ring-offset-0 hover:bg-blue-800 focus:ring-blue-300">
                                Add TimeSheet
                            </button>
                        </Link>
                    </div>

                    {isLoading && <div>Loading...</div>}

                    {timesheetdata.length > 0 && <TanstackTable data={timesheetdata} columns={columns} downloadBtn={true}></TanstackTable>}
                    {!isLoading && timesheetdata.length === 0 && <div className="my-5">No records found</div>}
                </div>
            </main>
        </>
    );
}
